import type { AxiosError } from 'axios';
import type { UseMutationResult } from 'react-query';
import { useMutation } from 'react-query';

import * as insightsApi from '@hotjar/webapp/domains/highlights/api';
import { queryClient } from '@hotjar/webapp/utils/react-query';

type LabelId = string;

function combineStatus(status: Array<UseMutationResult['status']>): UseMutationResult['status'] {
  if (status.includes('error')) {
    return 'error';
  }

  if (status.includes('loading')) {
    return 'loading';
  }

  if (status.includes('success')) {
    return 'success';
  }

  return 'idle';
}

interface UseHighlightLabelsHook {
  add: (labelId: LabelId) => void;
  remove: (labelId: LabelId) => void;
  status: UseMutationResult['status'];
}

export function useHighlightLabels(
  siteId: string | null,
  highlightId: string,
): UseHighlightLabelsHook {
  const onSuccess = () => {
    queryClient.invalidateQueries([insightsApi.SINGLE_HIGHLIGHT_QUERY_KEY, siteId, highlightId]);
    queryClient.invalidateQueries(insightsApi.HIGHLIGHTS_LIST_QUERY_KEY);
    queryClient.invalidateQueries(insightsApi.HEATMAP_HIGHLIGHTS_LIST_QUERY_KEY);
    queryClient.invalidateQueries(insightsApi.RECORDING_HIGHLIGHTS_LIST_QUERY_KEY);
  };

  const addMutation: UseMutationResult<void, AxiosError, LabelId> = useMutation(
    async (labelId) => {
      if (siteId === null) {
        throw new Error('site ID is not defined');
      }

      return insightsApi.addLabel(siteId, highlightId, labelId);
    },
    { onSuccess },
  );
  const removeMutation: UseMutationResult<void, AxiosError, LabelId> = useMutation(
    async (labelId) => {
      if (siteId === null) {
        throw new Error('site ID is not defined');
      }

      return insightsApi.removeLabel(siteId, highlightId, labelId);
    },
    { onSuccess },
  );

  return {
    add: addMutation.mutate,
    remove: removeMutation.mutate,
    status: combineStatus([addMutation.status, removeMutation.status]),
  };
}
